import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface ItemCfop {
  pnvLin: number; itsCod: number | null; descricao: string
  quantidade: number; valorTotal: number
  tnoCod: number | null; cfopAtual: number | null; naturezaDes: string
  itsEsp: number | null
}

interface NotaCfop {
  empcod: number; serie: string; numero: number; cliCod: string; cliNom: string
  modelo: string; chave: string | null
  tnoCod: number | null; cfopAtual: number | null; naturezaDes: string
  itens: ItemCfop[]
}

interface NotaListaItem {
  empcod: number; serie: string; numero: number; cliCod: string; cliNom: string
  modelo: string; dataEmissao: string | null; dataRecebimento: string | null; valorTotal: number
}

type Escopo = 'capa' | 'itens' | 'ambos'

function fmtMoeda(v: number) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtDataBr(v: string | null) {
  if (!v) return '  /  /  '
  return v.slice(0, 10).split('-').reverse().join('/')
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

// Mostra o CFOP (o que o usuário realmente reconhece), não só o código
// interno da natureza — a mesma empresa pode ter várias naturezas (códigos
// diferentes) todas usando o mesmo CFOP.
function renderNatureza(item: any) {
  return (
    <>
      <div className="nat-item-linha1">
        <span className="nat-item-cod">#{item.tno_cod}</span>
        <span className="nat-item-cfop">CFOP {item.tno_cdf}</span>
      </div>
      <div className="nat-item-dsc">{item.tno_des}</div>
    </>
  )
}
function descNatureza(item: any) {
  return `${item.tno_cdf} - ${item.tno_des}`
}

export default function AlterarCfopNotaFiscal() {
  const { empresaCod } = useEmpresa()
  const [chave, setChave] = useState('')
  const [serie, setSerie] = useState('')
  const [numero, setNumero] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')

  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState('')
  const [lista, setLista] = useState<NotaListaItem[] | null>(null)
  const [nota, setNota] = useState<NotaCfop | null>(null)

  const [escopo, setEscopo] = useState<Escopo>('ambos')
  const [novaNatCod, setNovaNatCod] = useState('')
  const [novaNatDes, setNovaNatDes] = useState('')
  const [overrideItens, setOverrideItens] = useState<Record<number, { cod: string; des: string }>>({})

  const [salvando, setSalvando] = useState(false)
  const [sucesso, setSucesso] = useState('')
  const [avisos, setAvisos] = useState<string[]>([])

  function limparResultado() {
    setNota(null); setLista(null); setEscopo('ambos')
    setNovaNatCod(''); setNovaNatDes(''); setOverrideItens({}); setAvisos([])
  }

  async function buscarDetalhe(ident: { chave?: string; empcod?: number; serie?: string; numero?: number; cliCod?: string }) {
    const r = await api.get('/erp/alterar-cfop-nota', { params: ident })
    setNota(r.data)
    setLista(null)
  }

  async function buscar() {
    setErro(''); setSucesso(''); limparResultado()
    const chaveDigitos = chave.replace(/\D/g, '')
    if (!chaveDigitos && !serie.trim() && !numero.trim() && !fornecedorCod) {
      setErro('Informe a Chave NF-e/CT-e, ou ao menos Série, Número ou Fornecedor.')
      return
    }
    setBuscando(true)
    try {
      if (chaveDigitos) {
        await buscarDetalhe({ chave: chaveDigitos })
        return
      }
      const params: Record<string, string | number> = { empcod: empresaCod ?? 1 }
      if (serie.trim()) params.serie = serie.trim()
      if (numero.trim()) params.numero = Number(numero)
      if (fornecedorCod) params.cliCod = fornecedorCod
      // Reaproveita a mesma busca ampla da tela "Alterar Data" — só número
      // lista as notas com esse número, só fornecedor lista as dele.
      const r = await api.get('/erp/alterar-data-nota/lista', { params })
      const encontradas: NotaListaItem[] = r.data
      if (encontradas.length === 0) {
        setErro('Nenhuma nota encontrada.')
      } else if (encontradas.length === 1) {
        const n = encontradas[0]
        await buscarDetalhe({ empcod: n.empcod, serie: n.serie, numero: n.numero, cliCod: n.cliCod })
      } else {
        setLista(encontradas)
      }
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar nota(s).')
    } finally {
      setBuscando(false)
    }
  }

  async function selecionarDaLista(item: NotaListaItem) {
    setErro(''); setBuscando(true)
    try {
      await buscarDetalhe({ empcod: item.empcod, serie: item.serie, numero: item.numero, cliCod: item.cliCod })
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar a nota.')
    } finally {
      setBuscando(false)
    }
  }

  function alterarOverride(pnvLin: number, cod: string, des: string) {
    setOverrideItens(prev => {
      const novo = { ...prev }
      if (cod) novo[pnvLin] = { cod, des }
      else delete novo[pnvLin]
      return novo
    })
  }

  async function confirmar() {
    if (!nota) return
    setErro(''); setSucesso(''); setAvisos([])
    const itensOverride = Object.entries(overrideItens).map(([lin, v]) => ({ pnvLin: Number(lin), novaNatureza: Number(v.cod) }))
    if (!novaNatCod && itensOverride.length === 0) {
      setErro('Informe a Nova Natureza de Operação (geral) ou ao menos um item com natureza individual.')
      return
    }
    setSalvando(true)
    try {
      const r = await api.post('/erp/alterar-cfop-nota', {
        empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod,
        escopo,
        novaNaturezaGeral: novaNatCod || undefined,
        itensOverride,
      })
      setSucesso(`CFOP/Natureza da nota ${nota.serie}/${nota.numero} alterado com sucesso.`)
      setAvisos(r.data?.avisos ?? [])
      limparResultado()
      setChave(''); setSerie(''); setNumero(''); setFornecedorCod(''); setFornecedorDes('')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao alterar o CFOP da nota.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Alterar CFOP das Notas Fiscais</h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.9rem' }}>
          <Campo rotulo="Chave NF-e/CT-e" width={420}>
            <input style={{ ...inp, fontFamily: 'monospace' }} value={chave} maxLength={44}
              onChange={e => setChave(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscar()}
              placeholder="44 dígitos" />
          </Campo>
        </div>

        <div style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '-0.5rem 0 0.9rem' }}>— ou —</div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Série" width={90}>
            <input style={inp} value={serie} onChange={e => setSerie(e.target.value)} />
          </Campo>
          <Campo rotulo="Número" width={130}>
            <input style={inp} inputMode="numeric" value={numero} onChange={e => setNumero(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && buscar()} />
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={340}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscando} onClick={buscar} style={{ height: 32 }}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {sucesso && <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}>✓ {sucesso}</div>}
      {avisos.map((a, i) => (
        <div key={i} className="alert alert-warning" style={{ marginBottom: '0.75rem' }}>⚠ {a}</div>
      ))}

      {lista && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
        }}>
          <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.6rem' }}>
            {lista.length} nota(s) encontrada(s) — clique na linha certa:
          </div>
          <table className="table" style={{ width: '100%', fontSize: '0.82rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: 60 }}>Série</th>
                <th style={{ textAlign: 'center', width: 90 }}>Número</th>
                <th style={{ textAlign: 'left' }}>Fornecedor</th>
                <th style={{ textAlign: 'center', width: 70 }}>Modelo</th>
                <th style={{ textAlign: 'center', width: 100 }}>Emissão</th>
                <th style={{ textAlign: 'center', width: 100 }}>Recebimento</th>
                <th style={{ textAlign: 'right', width: 120 }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((it, idx) => (
                <tr key={`${it.empcod}-${it.serie}-${it.numero}-${it.cliCod}`}
                  onClick={() => selecionarDaLista(it)}
                  style={{ cursor: 'pointer', background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <td style={{ textAlign: 'center' }}>{it.serie}</td>
                  <td style={{ textAlign: 'center' }}>{it.numero}</td>
                  <td>{it.cliNom} ({it.cliCod})</td>
                  <td style={{ textAlign: 'center' }}>{it.modelo}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDataBr(it.dataEmissao)}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDataBr(it.dataRecebimento)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a6ea8' }}>{fmtMoeda(it.valorTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nota && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.85rem' }}>
            <div><strong>Estab.:</strong> {nota.empcod} &nbsp; <strong>Série:</strong> {nota.serie} &nbsp; <strong>Número:</strong> {nota.numero}</div>
            <div><strong>Fornecedor:</strong> {nota.cliNom} ({nota.cliCod})</div>
            <div><strong>Modelo:</strong> {nota.modelo}</div>
            <div><strong>CFOP Atual:</strong> {nota.cfopAtual ?? '-'} ({nota.naturezaDes || '—'})</div>
          </div>

          <div style={{
            background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
            borderRadius: 6, padding: '0.85rem 1rem', marginBottom: '1rem',
          }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
              <Campo rotulo="Aplicar em" width={220}>
                <select style={inp} value={escopo} onChange={e => setEscopo(e.target.value as Escopo)}>
                  <option value="capa">Só a Capa da Nota</option>
                  <option value="itens">Só os Itens</option>
                  <option value="ambos">Capa e Itens</option>
                </select>
              </Campo>
              <Campo rotulo="Nova Natureza de Operação (geral)" width={320}>
                <LookupSelect
                  endpoint="/erp/naturezas-operacao/lista?entrada=1" codKey="tno_cod" descKey="tno_des"
                  renderItem={renderNatureza} buildDesc={descNatureza}
                  codValue={novaNatCod} descValue={novaNatDes}
                  onSelect={(cod, des) => { setNovaNatCod(cod); setNovaNatDes(des) }}
                  placeholder="Selecione..." listWidth={340}
                />
              </Campo>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Preencha a Nova Natureza geral e confirme, ou informe a "Nova Nat." de cada item na tabela abaixo pra alterar individualmente (tem prioridade sobre a geral).
            </div>
          </div>

          {nota.itens.length > 0 && (escopo === 'itens' || escopo === 'ambos' || Object.keys(overrideItens).length > 0) && (
            <table className="table" style={{ width: '100%', fontSize: '0.8rem', marginBottom: '1rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', width: 50 }}>Lin</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Item</th>
                  <th style={{ textAlign: 'left' }}>Descrição</th>
                  <th style={{ textAlign: 'right', width: 90 }}>Qtde</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Valor</th>
                  <th style={{ textAlign: 'center', width: 90 }}>CFOP Atual</th>
                  <th style={{ textAlign: 'left', width: 260 }}>Nova Nat. (opcional)</th>
                </tr>
              </thead>
              <tbody>
                {nota.itens.map((it, idx) => (
                  <tr key={it.pnvLin} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                    <td style={{ textAlign: 'center', color: '#6b7280' }}>{it.pnvLin}</td>
                    <td style={{ textAlign: 'center', color: '#6b7280' }}>{it.itsCod ?? '-'}</td>
                    <td>{it.descricao}</td>
                    <td style={{ textAlign: 'right' }}>{it.quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 3 })}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a6ea8' }}>{fmtMoeda(it.valorTotal)}</td>
                    <td style={{ textAlign: 'center' }}>{it.cfopAtual ?? '-'}</td>
                    <td>
                      <LookupSelect
                        endpoint={`/erp/naturezas-operacao/lista?entrada=1${it.itsCod ? `&csa=${it.itsEsp === 2 ? 'S' : 'C'}` : ''}`}
                        codKey="tno_cod" descKey="tno_des"
                        renderItem={renderNatureza} buildDesc={descNatureza}
                        codValue={overrideItens[it.pnvLin]?.cod ?? ''} descValue={overrideItens[it.pnvLin]?.des ?? ''}
                        onSelect={(cod, des) => alterarOverride(it.pnvLin, cod, des)}
                        placeholder="(usar a geral)" listWidth={320}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <button className="btn btn-primary btn-sm" disabled={salvando} onClick={confirmar} style={{ height: 32 }}>
            {salvando ? 'Salvando...' : 'Confirmar Alteração'}
          </button>
        </div>
      )}
    </div>
  )
}
